import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import { PLANS } from '../lib/plans';
import { trackEvent } from '../services/analytics';
import { setPendingCheckout } from '../lib/pendingCheckout';

/**
 * Drop-in CTA that takes the visitor straight to Stripe Checkout for a given plan.
 * - Authenticated users: opens Stripe immediately.
 * - Unauthenticated visitors: stash intent in sessionStorage and route to /auth.
 *   PaymentContext picks the intent up after sign-in and starts checkout automatically.
 */
const BuyButton = ({
  planId = 'trial',
  context = 'cta',
  variant = 'primary',
  className = '',
  children,
  showPrice = true,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { initiatePayment, hasAccess, accessTier } = usePayment();
  const [loading, setLoading] = useState(false);

  const plan = PLANS[planId];
  if (!plan) return null;

  const baseLabel = children
    || (planId === 'trial'
      ? (showPrice ? 'Try for £1' : 'Try it')
      : planId === 'lifetime'
        ? (showPrice ? 'Get Lifetime — £50' : 'Get lifetime')
        : (showPrice ? 'Subscribe £4.99/mo' : 'Subscribe'));

  const styles = {
    primary:
      'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-black text-white font-semibold text-sm hover:bg-black/90 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed',
    accent:
      'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-vibe-cyan to-cyan-400 text-black font-semibold text-sm hover:opacity-90 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed',
    compact:
      'inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black text-white font-semibold text-xs hover:bg-black/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed',
    ghost:
      'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white/80 text-black font-semibold text-sm hover:bg-white border border-black/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed',
  };

  const handleClick = async () => {
    if (loading) return;

    try {
      trackEvent('buy_cta_clicked', { plan: planId, context, authenticated: isAuthenticated });
    } catch {}

    // Already paying user → just take them to the dashboard.
    if (hasAccess && accessTier !== 'trial') {
      navigate('/dashboard');
      return;
    }

    if (!isAuthenticated) {
      setPendingCheckout(planId, context);
      navigate('/auth?redirect=/');
      return;
    }

    try {
      setLoading(true);
      await initiatePayment(planId, context);
      // initiatePayment redirects to Stripe; if it returns we just unblock.
    } catch (err) {
      console.error('BuyButton checkout failed:', err);
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`${styles[variant] || styles.primary} ${className}`}
      aria-label={typeof baseLabel === 'string' ? baseLabel : 'Start checkout'}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Redirecting…
        </>
      ) : (
        <>
          {baseLabel}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </>
      )}
    </button>
  );
};

export default BuyButton;
